import { useEffect, useRef, useState } from "react"

type Options = {
  intervalMs?: number
  onUpdate: () => Promise<void>
  onError?: (error: unknown) => void
}

/**
 * Update Connection を一定間隔で自動送信するためのフック。
 * popopo_client の Timer.periodic と同様に setTimeout チェインで動かし、
 * リクエストが重ならないようにする。
 *
 * - ON にした瞬間に即時 1 回送信し、以降 intervalMs 間隔で送信する
 * - onUpdate が throw した場合は onError を呼び、自動的に OFF に戻す
 * - enabled が false になる、または unmount されるとタイマーを停止する
 */
export const useAutoUpdateConnection = ({ intervalMs = 10_000, onUpdate, onError }: Options) => {
  const [enabled, setEnabled] = useState(false)
  const [lastSentAt, setLastSentAt] = useState<number | null>(null)

  const onUpdateRef = useRef(onUpdate)
  const onErrorRef = useRef(onError)
  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])
  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const run = async () => {
      if (cancelled) return
      try {
        await onUpdateRef.current()
      } catch (e) {
        if (cancelled) return
        onErrorRef.current?.(e)
        setEnabled(false)
        return
      }
      if (cancelled) return
      setLastSentAt(Date.now())
      timer = setTimeout(() => {
        void run()
      }, intervalMs)
    }

    void run()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [enabled, intervalMs])

  return { enabled, setEnabled, lastSentAt }
}
